import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Check, Cpu, Layers, Gauge, Settings, Download, Play, X } from 'lucide-react';
import Navbar from '../components/layout/Navbar';
import Footer from '../components/layout/Footer';
import CTA from '../components/sections/CTA';
import { Button } from '../components/ui/button';

const ConcretePage = () => {
  const canvasRef = useRef(null);
  const [showVideo, setShowVideo] = useState(false);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    let animationId;
    let offset = 0;

    const resize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };

    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.strokeStyle = 'rgba(220, 38, 38, 0.1)';
      ctx.lineWidth = 1;
      const gridSize = 60;
      offset = (offset + 0.3) % gridSize;
      for (let y = offset; y < canvas.height; y += gridSize) {
        ctx.beginPath();
        ctx.moveTo(0, y);
        ctx.lineTo(canvas.width, y);
        ctx.stroke();
      }
      for (let x = offset; x < canvas.width; x += gridSize) {
        ctx.beginPath();
        ctx.moveTo(x, 0);
        ctx.lineTo(x, canvas.height);
        ctx.stroke();
      }
      animationId = requestAnimationFrame(animate);
    };

    resize();
    animate();
    window.addEventListener('resize', resize);
    return () => {
      cancelAnimationFrame(animationId);
      window.removeEventListener('resize', resize);
    };
  }, []);

  const features = [
    { icon: Cpu, title: 'Materiales Locales Adaptables', description: 'Sistema diseñado para trabajar con materiales disponibles localmente.' },
    { icon: Layers, title: 'Enfoque Industrial Escalable', description: 'Pensado para producción en escala, ideal para desarrollos masivos.' },
    { icon: Gauge, title: 'Alta Productividad', description: 'Velocidad de impresión optimizada para cumplir plazos exigentes.' },
    { icon: Settings, title: 'Tecnología Alemana', description: 'Maquinaria con estándares europeos de precisión y confiabilidad.' }
  ];

  const applications = [
    { title: 'Muros perimetrales', price: 'desde u$s 195/m²' },
    { title: 'Viviendas unifamiliares', price: 'desde u$s 350/m²' },
    { title: 'Estructuras industriales', price: 'consultar' },
    { title: 'Elementos arquitectónicos', price: 'consultar' }
  ];

  return (
    <div className="min-h-screen">
      <Navbar />
      <main>
        {/* Hero Section with Machine Image */}
        <section className="relative min-h-[80vh] flex items-center overflow-hidden bg-neutral-950">
          <canvas ref={canvasRef} className="absolute inset-0 z-0" />
          <div className="absolute inset-0 bg-gradient-to-b from-neutral-950/80 via-neutral-950/50 to-neutral-950 z-10" />
          <div className="relative z-20 max-w-7xl mx-auto px-6 py-32">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <span className="inline-flex items-center gap-2 bg-red-600/20 border border-red-500/30 rounded-full px-4 py-2 mb-6">
                  <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
                  <span className="text-red-400 text-sm font-medium">Tecnología COSMOS</span>
                </span>
                <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold text-white mb-6">
                  Impresión 3D
                  <span className="block text-red-500">de Hormigón</span>
                </h1>
                <p className="text-xl text-neutral-400 mb-8 max-w-xl">Fabricación de muros, perímetros y estructuras complejas con tecnología alemana bajo estándares europeos.</p>
                <div className="flex flex-wrap gap-4">
                  <Button size="lg" className="bg-red-600 hover:bg-red-700 text-white rounded-full" asChild>
                    <Link to="/contact">Solicitar Propuesta <ArrowRight className="ml-2" size={18} /></Link>
                  </Button>
                  <Button size="lg" variant="outline" className="border-white/30 text-white hover:bg-white hover:text-neutral-900 rounded-full">
                    <Download className="mr-2" size={18} />Dossier Técnico
                  </Button>
                </div>
              </div>
              <div className="relative">
                {/* Machine Image with Glow Effect - Zoomed out */}
                <div className="relative rounded-3xl overflow-hidden group">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_modular-expert/artifacts/ac8vgd9y_IMG_2138.JPG" 
                    alt="Impresora 3D de Hormigón COSMOS construyendo muro" 
                    className="w-full h-auto rounded-3xl shadow-2xl transition-transform duration-700 group-hover:scale-105"
                    style={{ objectFit: 'cover', objectPosition: 'center', transform: 'scale(0.85)' }}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-red-600/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                  <div className="absolute inset-0 rounded-3xl ring-2 ring-red-500/0 group-hover:ring-red-500/50 transition-all duration-500" />
                </div>
                <div className="absolute -top-4 -right-4 bg-red-600 text-white px-4 py-2 rounded-full shadow-lg animate-bounce">
                  <p className="text-sm font-bold">desde u$s 195/m²</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Features Section */}
        <section className="py-24 bg-white">
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center mb-16">
              <span className="text-red-600 text-sm font-semibold uppercase tracking-wider">Características</span>
              <h2 className="text-4xl md:text-5xl font-bold text-neutral-900 mt-4">Tecnología de Vanguardia</h2>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              {features.map((feature, index) => {
                const Icon = feature.icon;
                return (
                  <div key={index} className="group p-6 rounded-2xl bg-neutral-50 hover:bg-neutral-900 transition-all duration-500 hover:-translate-y-2">
                    <div className="w-14 h-14 rounded-xl bg-red-100 group-hover:bg-red-600 flex items-center justify-center mb-4 transition-colors">
                      <Icon className="text-red-600 group-hover:text-white transition-colors" size={28} />
                    </div>
                    <h3 className="text-xl font-bold text-neutral-900 group-hover:text-white mb-2 transition-colors">{feature.title}</h3>
                    <p className="text-neutral-600 group-hover:text-neutral-300 transition-colors">{feature.description}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* Video & House Section */}
        <section className="py-24 bg-neutral-50">
          <div className="max-w-7xl mx-auto px-6">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              {/* Video Preview */}
              <div className="relative group cursor-pointer" onClick={() => setShowVideo(true)}>
                <div className="relative rounded-3xl overflow-hidden shadow-2xl">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_modular-expert/artifacts/0oq91mt7_PHOTO-2025-10-24-23-26-13.jpg" 
                    alt="Casa construida con impresión 3D" 
                    className="w-full h-auto transition-transform duration-700 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-black/30 group-hover:bg-black/40 transition-colors flex items-center justify-center">
                    <div className="w-20 h-20 rounded-full bg-red-600 flex items-center justify-center shadow-xl group-hover:scale-110 transition-transform">
                      <Play className="text-white ml-1" size={36} fill="white" />
                    </div>
                  </div>
                </div>
                <p className="text-center text-neutral-600 mt-4 text-sm">Click para ver el video</p>
              </div>

              {/* Content */}
              <div>
                <span className="text-red-600 text-sm font-semibold uppercase tracking-wider">Resultado Final</span>
                <h2 className="text-4xl md:text-5xl font-bold text-neutral-900 mt-4 mb-6">Casa Construida con Impresión 3D</h2>
                <p className="text-neutral-600 text-lg mb-8 leading-relaxed">
                  Mirá el resultado de nuestra tecnología de impresión 3D de hormigón. Muros estructurales 
                  de alta resistencia, terminaciones de primera calidad, y tiempos de construcción reducidos 
                  en un 70% comparado con métodos tradicionales.
                </p>
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-white p-4 rounded-xl shadow-sm">
                    <p className="text-3xl font-bold text-red-600">70%</p>
                    <p className="text-neutral-600 text-sm">Más rápido</p>
                  </div>
                  <div className="bg-white p-4 rounded-xl shadow-sm">
                    <p className="text-3xl font-bold text-red-600">-60%</p>
                    <p className="text-neutral-600 text-sm">Reducción de costos</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Applications Section */}
        <section className="py-24 bg-neutral-950">
          <div className="max-w-7xl mx-auto px-6">
            <div className="grid lg:grid-cols-2 gap-16 items-center">
              <div>
                <span className="text-red-500 text-sm font-semibold uppercase tracking-wider">Aplicaciones</span>
                <h2 className="text-4xl md:text-5xl font-bold text-white mt-4 mb-8">¿Qué podemos construir?</h2>
                <div className="space-y-4">
                  {applications.map((app, index) => (
                    <div key={index} className="flex items-center justify-between p-4 bg-white/5 rounded-xl border border-white/10 hover:border-red-500/50 hover:bg-white/10 transition-all duration-300 transform hover:translate-x-2">
                      <div className="flex items-center gap-3">
                        <Check className="text-red-500" size={20} />
                        <span className="text-white font-medium">{app.title}</span>
                      </div>
                      <span className="text-red-400 font-semibold">{app.price}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div className="relative">
                <img 
                  src="https://customer-assets.emergentagent.com/job_modular-expert/artifacts/0oq91mt7_PHOTO-2025-10-24-23-26-13.jpg" 
                  alt="Casa 3D Printed" 
                  className="rounded-3xl shadow-2xl"
                />
                <div className="absolute -bottom-6 -left-6 bg-red-600 text-white p-6 rounded-2xl shadow-xl">
                  <p className="text-3xl font-bold">70%</p>
                  <p className="text-sm opacity-80">Más rápido</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        <CTA />
      </main>
      <Footer />

      {/* Video Modal */}
      {showVideo && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 p-4" onClick={() => setShowVideo(false)}>
          <div className="relative w-full max-w-4xl" onClick={e => e.stopPropagation()}>
            <button 
              onClick={() => setShowVideo(false)} 
              className="absolute -top-12 right-0 text-white hover:text-red-500 transition-colors"
            >
              <X size={32} />
            </button>
            <video 
              controls 
              autoPlay 
              className="w-full rounded-2xl shadow-2xl"
              src="https://customer-assets.emergentagent.com/job_modular-expert/artifacts/szfp6h98_1bfb5d35-2aac-4875-b12d-addcb62f1ffa.MP4"
            >
              Tu navegador no soporta video HTML5.
            </video>
          </div>
        </div>
      )}
    </div>
  );
};

export default ConcretePage;
